import React from 'react'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import Grid from '@material-ui/core/Grid'
import MenuItem from '@material-ui/core/MenuItem'
import { makeStyles } from '@material-ui/core'
import { ModalDialog } from '../../../components/common/Dialogs'
import { Button } from '../../../components/common/Buttons'
import { TextField, SelectField, Switch } from '../../../components/common/Inputs'
import { Paper, SpacingContainer } from '../../../components/common/Containers'
import Typography from '../../../components/common/Typography'
import { useForm } from '../../../hooks/useForm'
import { useTrans } from '../../../services/i18n'
import { useAlertContext } from '../../../contexts/AlertContext'
import { EVENT_TYPE_OPTIONS, EVENT_STATUS_OPTIONS, EVENT_STATUS_DRAFT } from '../../../constants'

const formOptions = { enableReinitialize: true, validateOnMount: false }

const useStyles = makeStyles({
  pickerField: {
    '& input::-webkit-calendar-picker-indicator': { filter: 'none' },
  },
})

const getInitialValues = (event) => ({
  name: event ? event.name : '',
  type: event ? event.type : '',
  status: event ? event.status : EVENT_STATUS_DRAFT,
  startDate: event ? dayjs(event.startAt).format('YYYY-MM-DD') : '',
  startTime: event ? dayjs(event.startAt).format('HH:mm') : '',
  endDate: event ? dayjs(event.endAt).format('YYYY-MM-DD') : '',
  endTime: event ? dayjs(event.endAt).format('HH:mm') : '',
  location: event ? event.location : '',
  registrationLink: event?.registrationLink || '',
  sendSystemAlert: Boolean(event?.sendSystemAlert),
})

const EventFormModal = ({ open, event, onClose, onCreate, onUpdate, onSaved }) => {
  const { trans } = useTrans()
  const styles = useStyles()
  const { successAlert, errorAlert } = useAlertContext()

  const schema = React.useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(trans('required')),
        type: Yup.string().required(trans('required')),
        status: Yup.string().required(trans('required')),
        startDate: Yup.string().required(trans('required')),
        startTime: Yup.string().required(trans('required')),
        endDate: Yup.string().required(trans('required')),
        endTime: Yup.string()
          .required(trans('required'))
          .test('end-after-start', trans('end-must-be-after-start'), (value, context) => {
            const { startDate, startTime, endDate } = context.parent
            if (!value || !startDate || !startTime || !endDate) return true
            return dayjs(`${endDate}T${value}`).isAfter(dayjs(`${startDate}T${startTime}`))
          }),
        location: Yup.string().required(trans('required')),
        registrationLink: Yup.string()
          .transform((value) => (value === '' ? null : value))
          .nullable()
          .url(trans('invalid-url')),
        sendSystemAlert: Yup.boolean().default(false),
      }),
    [trans]
  )

  const onSubmit = (values) => {
    const payload = {
      name: values.name,
      type: values.type,
      status: values.status,
      startAt: dayjs(`${values.startDate}T${values.startTime}`).toISOString(),
      endAt: dayjs(`${values.endDate}T${values.endTime}`).toISOString(),
      location: values.location,
      registrationLink: values.registrationLink || null,
      sendSystemAlert: Boolean(values.sendSystemAlert),
    }
    const action = event ? onUpdate({ id: event.id, ...payload }) : onCreate(payload)
    return Promise.resolve(action).then((res) => {
      if (!res) return
      if (event && res.payload === null) {
        errorAlert(trans('event-no-longer-exists'))
        onClose()
        onSaved()
        return
      }
      if (res.payload) {
        successAlert(trans(event ? 'event-updated' : 'event-created'))
        onClose()
        onSaved()
      }
    })
  }

  const { getFieldProps, handleSubmit, isSubmitting, resetForm, setFieldValue, values } = useForm(
    schema,
    getInitialValues(event),
    onSubmit,
    formOptions
  )

  React.useLayoutEffect(() => {
    if (open) resetForm({ values: getInitialValues(event) })
  }, [open])

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <ModalDialog
      open={open}
      title={trans(event ? 'edit-event' : 'add-event')}
      onClose={handleClose}
      actions={
        <>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {trans('cancel')}
          </Button>
          <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
            {trans('save')}
          </Button>
        </>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label={trans('event-name')}
            placeholder={trans('required')}
            required
            maxLength={255}
            {...getFieldProps('name')}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography weight="500" fontFamily="lexend" color="label" gutter="label" variant="body2">
            {trans('type')}
          </Typography>
          <SelectField style={{ marginTop: 7 }} placeholder={trans('required')} {...getFieldProps('type')}>
            {EVENT_TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={6}>
          <Typography weight="500" fontFamily="lexend" color="label" gutter="label" variant="body2">
            {trans('status')}
          </Typography>
          <SelectField style={{ marginTop: 7 }} placeholder={trans('required')} {...getFieldProps('status')}>
            {EVENT_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={6}>
          <TextField className={styles.pickerField} label={trans('start-date')} type="date" required {...getFieldProps('startDate')} />
        </Grid>
        <Grid item xs={6}>
          <TextField className={styles.pickerField} label={trans('start-time')} type="time" required {...getFieldProps('startTime')} />
        </Grid>
        <Grid item xs={6}>
          <TextField className={styles.pickerField} label={trans('end-date')} type="date" required {...getFieldProps('endDate')} />
        </Grid>
        <Grid item xs={6}>
          <TextField className={styles.pickerField} label={trans('end-time')} type="time" required {...getFieldProps('endTime')} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={trans('location')}
            placeholder={trans('required')}
            required
            maxLength={255}
            {...getFieldProps('location')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={`${trans('registration-link')} (${trans('optional')})`}
            placeholder="https://"
            maxLength={2048}
            {...getFieldProps('registrationLink')}
          />
        </Grid>
        <Grid item xs={12}>
          <Paper
            bgColor="paperSecondary"
            component={SpacingContainer}
            px={2}
            py={2}
            style={{ borderRadius: 12 }}
          >
            <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
              <Grid item xs style={{ paddingRight: 16 }}>
                <Typography weight="600" fontFamily="lexend" variant="body1">
                  {trans('send-a-system-alert')}
                </Typography>
                <Typography variant="body2" color="hint" style={{ marginTop: 4 }}>
                  {trans('send-system-alert-helper')}
                </Typography>
              </Grid>
              <Grid item xs="auto">
                <Switch
                  checked={Boolean(values.sendSystemAlert)}
                  onChange={(e) => setFieldValue('sendSystemAlert', e.target.checked)}
                  name="sendSystemAlert"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </ModalDialog>
  )
}

EventFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    status: PropTypes.string,
    startAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    location: PropTypes.string,
    registrationLink: PropTypes.string,
    sendSystemAlert: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
}

EventFormModal.defaultProps = {
  event: null,
}

export default EventFormModal
